import { useState } from 'react';
import { Outlet, useLocation, Link, Navigate } from 'react-router-dom';
import { Layout, Breadcrumb } from 'antd';
import { CustomSider } from './CustomSider';
import { Header } from './Header';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { resources } from '../../config/resources';
import { hasPermission } from '../../hooks/usePermission';
import { tokenManager } from '../../lib/api-client';

const { Content } = Layout;

// Build route -> permission map from resource definitions (single source of truth)
const routePermissions: { route: string; group: string; action: string }[] = [];
for (const r of resources) {
  const perm = r.meta?.permission as { group: string; action: string } | undefined;
  const route = typeof r.list === 'string' ? r.list : undefined;
  if (perm && route) {
    routePermissions.push({ route, ...perm });
  }
}
// Sort by route length descending so longer (more specific) routes match first
routePermissions.sort((a, b) => b.route.length - a.route.length);

const routeLabels: Record<string, string> = {
  '': 'Bosh sahifa',
  users: 'Foydalanuvchilar',
  passengers: "Yo'lovchilar",
  drivers: 'Haydovchilar',
  documents: 'Hujjatlar',
  trips: 'Sayohatlar',
  'driver-posted-trips': 'Haydovchi sayohatlari',
  'driver-trip-bookings': 'Bronlar',
  support: 'Murojatlar',
  emergency: 'Favqulodda',
  suggestions: 'Takliflar',
  faq: 'FAQ',
  'promo-codes': 'Promo-kodlar',
  'tracking-links': 'Marketing havolalari',
  staff: 'Xodimlar',
  settings: 'Sozlamalar',
  payments: "To'lovlar",
  analytics: 'Analitika',
  notifications: 'Bildirishnomalar',
  regions: 'Viloyatlar',
  cities: 'Shaharlar',
  districts: 'Tumanlar',
  vehicles: 'Transportlar',
  referrals: 'Referallar',
  incentives: 'Rag\'batlar',
  'bus-companies': 'Avtobus kompaniyalari',
  'bus-trips': 'Avtobus reislari',
  'bus-bookings': 'Avtobus bronlari',
  'bus-routes': 'Avtobus marshrutlari',
  create: 'Yaratish',
  edit: 'Tahrirlash',
};

export const CustomLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  // Route-level permission guard: redirect unauthorized users to dashboard
  const user = tokenManager.getUser();
  if (user && !user.isSuperAdmin && user.userType !== 'bus_company') {
    const matched = routePermissions.find(
      (rp) => rp.route !== '/' && location.pathname.startsWith(rp.route),
    );
    if (matched && !hasPermission(matched.group as any, matched.action as any)) {
      return <Navigate to="/" replace />;
    }
  }

  const pathSegments = location.pathname.split('/').filter(Boolean);

  const breadcrumbItems = [
    {
      title: <Link to="/">Bosh sahifa</Link>,
    },
    ...pathSegments.map((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const isLast = index === pathSegments.length - 1;
      const label = routeLabels[segment] || (/^\d+$/.test(segment) ? 'Batafsil' : segment);

      return {
        title: isLast ? (
          <span>{label}</span>
        ) : (
          <Link to={path}>{label}</Link>
        ),
      };
    }),
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <CustomSider collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout
        style={{
          marginLeft: collapsed ? 80 : 260,
          transition: 'margin-left 0.2s ease',
        }}
      >
        <Header />
        {pathSegments.length > 0 && (
          <div
            style={{
              padding: '0 24px',
              marginBottom: 0,
              background: '#fff',
              borderBottom: '1px solid #f0f0f0',
              height: 40,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Breadcrumb items={breadcrumbItems} />
          </div>
        )}
        <Content
          style={{
            margin: 24,
            minHeight: 'calc(100vh - 64px - 48px)',
            background: '#f0f2f5',
          }}
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
};
